// Data layer — thin fetch wrapper around /api/v1/* with CSRF + session cookies.
//
// Also provides `useResource(path)` for GET + loading/error states, and a
// `demoMode` flag (triggered by ?demo=1 in the URL) that falls back to MOCK
// data so the design preview keeps working without a server.
import { useEffect, useState, useCallback, useRef } from "react";
import { MOCK } from "./data.jsx";

const API_BASE = "/api/v1";

// ---------- Navigation -------------------------------------------------
//
// Single source of truth for route changes. Dispatches a `vz:navigate`
// CustomEvent that the root App listens for and turns into a setRoute.
// This replaces the old "localStorage.setItem + location.reload()" pattern
// which caused jarring full-page refreshes (and, worse, felt like "nothing
// happened" when the target screen rendered the same MOCK fallback).
//
// Usage:
//   navigate("dashboard")
//   navigate("results", { analysisId: 42 })
//   navigate("submission", { submissionId: "uuid..." })
//   navigate("auth", { authMode: "register" })
//
// Pass `hard: true` to force a full reload (used after login/logout where
// we need to flush in-memory caches).
export function navigate(route, extra = {}) {
  try {
    if (extra.analysisId != null) localStorage.setItem("vz-analysis-id", String(extra.analysisId));
    if (extra.submissionId != null) localStorage.setItem("vz-submission-id", String(extra.submissionId));
    if (extra.authMode) localStorage.setItem("vz-auth-mode", extra.authMode);
    if (route) localStorage.setItem("vz-route", route);
  } catch { /* ignore */ }

  if (extra.hard) {
    location.reload();
    return;
  }

  window.dispatchEvent(new CustomEvent("vz:navigate", { detail: { route, extra } }));
  try { window.scrollTo({ top: 0, behavior: "instant" }); }
  catch { try { window.scrollTo(0, 0); } catch { /* ignore */ } }
}

// ---------- CSRF --------------------------------------------------------
//
// Django in this project runs with CSRF_USE_SESSIONS=True +
// CSRF_COOKIE_HTTPONLY=True, which means:
//   (1) the token is NOT in a JS-readable cookie, and
//   (2) even when a cookie is set, it's HttpOnly and invisible to JS.
// So the SPA's HTML template (spa.html) injects the token via a
// <meta name="csrf-token"> tag and a window.__CSRF_TOKEN__ global.
// Every unsafe-method request reads from there.
//
// The cookie fallback stays for the ?demo=1 preview and for any older
// setting combinations where a cookie is actually populated.
function readCookie(name) {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}
function csrfToken() {
  if (typeof window !== "undefined" && window.__CSRF_TOKEN__) {
    return window.__CSRF_TOKEN__;
  }
  try {
    const meta = document.querySelector('meta[name="csrf-token"]');
    if (meta && meta.content) return meta.content;
  } catch { /* ignore */ }
  return readCookie("csrftoken") || "";
}

// ---------- Demo mode ---------------------------------------------------
export function isDemoMode() {
  try {
    return new URLSearchParams(location.search).get("demo") === "1";
  } catch {
    return false;
  }
}

// ---------- Errors ------------------------------------------------------
export class ApiError extends Error {
  constructor(status, body, path) {
    super(typeof body === "string" ? body : body?.error || `HTTP ${status}`);
    this.status = status;
    this.body = body;
    this.path = path;
  }
}

// ---------- Core request ------------------------------------------------
async function request(path, { method = "GET", body, headers = {}, signal } = {}) {
  const isJson = body && !(body instanceof FormData);
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      ...(isJson ? { "Content-Type": "application/json" } : {}),
      ...(method !== "GET" && method !== "HEAD" ? { "X-CSRFToken": csrfToken() } : {}),
      ...headers,
    },
    body: body == null ? undefined : isJson ? JSON.stringify(body) : body,
    signal,
  });

  const text = await res.text();
  let parsed = text;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    /* keep raw text */
  }

  if (!res.ok) {
    throw new ApiError(res.status, parsed, path);
  }
  return parsed;
}

export const apiGet = (path, opts) => request(path, { ...opts, method: "GET" });
export const apiPost = (path, body, opts) => request(path, { ...opts, method: "POST", body });
export const apiPatch = (path, body, opts) => request(path, { ...opts, method: "PATCH", body });
export const apiDelete = (path, opts) => request(path, { ...opts, method: "DELETE" });

// ---------- Demo data fallback -----------------------------------------
// When ?demo=1 is in the URL, useResource returns MOCK-derived data
// instead of hitting the server. Keeps the design preview functional
// while the backend is being wired up.
const DEMO_RESOURCES = {
  "/me/": () => ({
    id: 0,
    email: MOCK.user.email,
    name: MOCK.user.name,
    initials: MOCK.user.initials,
    role: MOCK.user.role,
    account_type: "investor",
    is_admin: false,
    is_investor_approved: true,
    company_name: MOCK.user.company,
    company_id: null,
  }),
  "/dashboard/": () => ({
    kpis: {
      decks_screened_30d: MOCK.stats.decksScreened,
      avg_ias: MOCK.stats.avgIAS,
      in_diligence: MOCK.stats.inDiligence,
      pipeline_value: MOCK.stats.pipelineValue,
    },
    pipeline_mix: {
      review: MOCK.companies.filter(c => c.status === "review").length,
      shortlist: MOCK.companies.filter(c => c.status === "shortlist").length,
      diligence: MOCK.companies.filter(c => c.status === "diligence").length,
      pass: MOCK.companies.filter(c => c.status === "pass").length,
    },
    triage: MOCK.companies.slice(0, 10).map(c => ({
      id: c.id, name: c.name, type: c.type, stage: c.stage, ias: c.ias,
      status: c.status, tech: c.tech, submitted: c.submitted,
      sector: c.sector, inv: c.inv,
    })),
    today_rail: MOCK.companies.slice(0, 3).map(c => ({
      id: c.id, name: c.name, type: c.type, stage: c.stage, ias: c.ias,
      status: c.status, tech: c.tech, submitted: c.submitted,
      sector: c.sector, inv: c.inv,
    })),
    activity: MOCK.activity.map(a => ({
      at: a.at, actor: a.who, action: a.kind,
      note: a.what, analysis_id: null,
    })),
  }),
  "/analyses/": () => ({
    results: MOCK.companies.map(c => ({
      id: c.id, name: c.name, type: c.type, stage: c.stage, ias: c.ias,
      status: c.status, tech: c.tech, submitted: c.submitted,
      sector: c.sector, inv: c.inv,
    })),
    count: MOCK.companies.length,
  }),
};

function demoResponse(path) {
  // Exact-match lookup first
  if (DEMO_RESOURCES[path]) return DEMO_RESOURCES[path]();
  // Strip query string, try again
  const pathOnly = path.split("?")[0];
  if (DEMO_RESOURCES[pathOnly]) return DEMO_RESOURCES[pathOnly]();
  return null;
}

// ---------- Hook: useResource ------------------------------------------
// Generic GET hook. Returns { data, loading, error, reload }.
// Pass `deps` to refetch when inputs change.
// If ?demo=1 is active and we have a demo fallback for this path,
// returns mock data after a micro-tick (simulating async).
export function useResource(path, { deps = [], enabled = true } = {}) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const abortRef = useRef(null);

  const reload = useCallback(async () => {
    if (!enabled) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setState(s => ({ ...s, loading: true, error: null }));

    if (isDemoMode()) {
      const demo = demoResponse(path);
      if (demo != null) {
        await Promise.resolve();
        setState({ data: demo, loading: false, error: null });
        return;
      }
    }

    try {
      const data = await apiGet(path, { signal: ctrl.signal });
      setState({ data, loading: false, error: null });
    } catch (err) {
      if (err.name === "AbortError") return;
      setState({ data: null, loading: false, error: err });
    }
  }, [path, enabled]);

  useEffect(() => {
    reload();
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, enabled, ...deps]);

  return { ...state, reload };
}

// ---------- Me (current user) ------------------------------------------
export function useMe() {
  return useResource("/me/");
}
